/**
 * Полный набор ISO 3166-1 alpha-2 кодов (≈249 стран и территорий).
 * Имена и флаги мы НЕ держим вручную — иначе при каждом новом городе
 * приходится дописывать (как было с VN, ID). Имена берём через
 * Intl.DisplayNames (есть в Node 20+ и во всех современных браузерах),
 * флаги — через Regional Indicator unicode-эмодзи (формула, не таблица).
 *
 * Список самих кодов всё-таки приходится держать самостоятельно: Intl
 * не отдаёт enumeration кодов, только перевод по коду. Список зафрозен
 * на момент 2026-05 (ISO 3166-1 + Kosovo XK как widely-used user-assigned).
 */
export const ALPHA2_CODES: ReadonlyArray<string> = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'XK', // Kosovo — user-assigned, де-факто стандарт во многих системах
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
];

/**
 * Локализованное имя страны через Intl.DisplayNames. Если код невалиден или
 * Intl по какой-то причине не вернул имя — fallback к самому коду.
 *
 * Кэшируем форматтер per-locale: создавать его per-call дорого (десятки мс на
 * больших combobox-списках).
 */
const displayNamesCache = new Map<string, Intl.DisplayNames>();
export function countryName(code: string, locale = 'ru'): string {
  const upper = code.toUpperCase();
  // Intl.DisplayNames бросает RangeError на странных входах (например, 'ZZZ').
  // Делаем pre-check, чтобы не выкидывать исключение на ровном месте.
  if (!/^[A-Z]{2}$/.test(upper)) return code;
  let dn = displayNamesCache.get(locale);
  if (!dn) {
    dn = new Intl.DisplayNames([locale], { type: 'region' });
    displayNamesCache.set(locale, dn);
  }
  try {
    return dn.of(upper) ?? code;
  } catch {
    return code;
  }
}

/**
 * Emoji-флаг из ISO 3166-1 alpha-2 кода через Regional Indicator characters.
 * Работает для любого валидного 2-буквенного кода без data-таблицы.
 * Пример: 'VN' → 🇻🇳, 'RU' → 🇷🇺.
 */
export function flagFromAlpha2(code: string): string {
  const upper = code.toUpperCase();
  if (upper.length !== 2 || !/^[A-Z]{2}$/.test(upper)) return '🏳️';
  // 'A' (65) → Regional Indicator A (0x1F1E6 = 127462)
  const base = 0x1f1e6 - 65;
  return String.fromCodePoint(base + upper.charCodeAt(0), base + upper.charCodeAt(1));
}

/**
 * Готовый список { code, name, flag } для UI. Имена локализованы под `locale`,
 * по умолчанию ru. Отсортирован по локализованному имени — combobox показывает
 * страны в естественном алфавитном порядке.
 */
export function getCountries(locale = 'ru'): ReadonlyArray<{ code: string; name: string; flag: string }> {
  const collator = new Intl.Collator(locale);
  return ALPHA2_CODES.map((code) => ({
    code,
    name: countryName(code, locale),
    flag: flagFromAlpha2(code),
  })).sort((a, b) => collator.compare(a.name, b.name));
}

/**
 * Снапшот списка стран на ru — для совместимости с уже написанным UI-кодом,
 * который ожидает массив, а не функцию. Вычисляется один раз при импорте.
 */
export const COUNTRIES = getCountries('ru');
